import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const RejectedLoans = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [selectedLoanSchedule, setSelectedLoanSchedule] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [nicFilter, setNicFilter] = useState('');

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/officer/loans', {
        params: { officerId: user.userId },
      });
      // Filter only REJECTED loans
      const rejectedLoans = (response.data || []).filter(loan => loan.status === 'REJECTED');
      // Sort by created date (newest first)
      const sortedLoans = rejectedLoans.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
        const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
        return dateB - dateA;
      });
      setLoans(sortedLoans);
    } catch (error) {
      console.error('Error fetching rejected loans:', error);
    } finally {
      setLoading(false);
    }
  };

  const openDetailsDialog = async (loan) => {
    setSelectedLoan(loan);
    setSelectedLoanSchedule(null);
    setLoadingSchedule(true);
    setOpenDetails(true);
    
    try {
      const response = await api.get(`/officer/loans/${loan.id}/schedule`);
      setSelectedLoanSchedule(response.data);
    } catch (error) {
      console.error('Error fetching loan schedule:', error);
    } finally {
      setLoadingSchedule(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter loans by NIC
  const filteredLoans = loans.filter((loan) => {
    if (!nicFilter.trim()) return true;
    const customer = loan.customer;
    const nic = customer?.nic || '';
    return nic.toLowerCase().includes(nicFilter.toLowerCase().trim());
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/officer/dashboard')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              color: '#0D47A1',
              fontSize: { xs: '1.75rem', sm: '2rem' },
            }}
          >
            Rejected Loans
          </Typography>
        </Box>
      </Box>

      {/* Summary Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">Total Rejected Loans</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'error.main' }}>
                {loans.length}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">Total Amount</Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Rs {loans.reduce((sum, loan) => sum + (parseFloat(loan.requestedAmount || 0)), 0).toFixed(2)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filter */}
      <TextField
        fullWidth
        placeholder="Search by Customer NIC..."
        value={nicFilter}
        onChange={(e) => setNicFilter(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Loans Table */}
      {filteredLoans.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="textSecondary">
            No rejected loans found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Loan ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>NIC</TableCell>
                <TableCell>Requested Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Rejection Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLoans.map((loan) => (
                <TableRow 
                  key={loan.id}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                  onClick={() => openDetailsDialog(loan)}
                >
                  <TableCell>{loan.id}</TableCell>
                  <TableCell>{loan.customer?.fullName || loan.customer?.name || 'N/A'}</TableCell>
                  <TableCell>{loan.customer?.nic || 'N/A'}</TableCell>
                  <TableCell>Rs {loan.requestedAmount?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    <Chip
                      label={loan.status}
                      color={getStatusColor(loan.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {loan.createdAt
                      ? new Date(loan.createdAt).toLocaleDateString()
                      : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {loan.notes ? (
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {loan.notes}
                      </Typography>
                    ) : 'N/A'}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openDetailsDialog(loan)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={openDetails} 
        onClose={() => setOpenDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Rejected Loan Details - ID: {selectedLoan?.id}
        </DialogTitle>
        <DialogContent>
          {selectedLoan && (
            <Box>
              {/* Loan Information */}
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Loan Information</Typography>
                  <Typography><strong>Loan ID:</strong> {selectedLoan.id}</Typography>
                  <Typography><strong>Customer:</strong> {selectedLoan.customer?.fullName || selectedLoan.customer?.name}</Typography>
                  <Typography><strong>NIC:</strong> {selectedLoan.customer?.nic}</Typography>
                  <Typography><strong>Phone:</strong> {selectedLoan.customer?.phoneNumber}</Typography>
                  <Typography><strong>Requested Amount:</strong> Rs {selectedLoan.requestedAmount?.toFixed(2)}</Typography>
                  <Typography><strong>Status:</strong> 
                    <Chip label={selectedLoan.status} color={getStatusColor(selectedLoan.status)} size="small" sx={{ ml: 1 }} />
                  </Typography>
                  <Typography><strong>Created:</strong> {selectedLoan.createdAt ? new Date(selectedLoan.createdAt).toLocaleString() : 'N/A'}</Typography>
                  <Typography><strong>Updated:</strong> {selectedLoan.updatedAt ? new Date(selectedLoan.updatedAt).toLocaleString() : 'N/A'}</Typography>
                  {selectedLoan.notes && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="subtitle2" gutterBottom><strong>Rejection Notes:</strong></Typography>
                      <Typography variant="body2">{selectedLoan.notes}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Repayment Schedule (if available) */}
              {loadingSchedule ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : selectedLoanSchedule && selectedLoanSchedule.installments && selectedLoanSchedule.installments.length > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>Repayment Schedule</Typography>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Due Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedLoanSchedule.installments
                          ?.filter(inst => inst.installmentNumber > 1)
                          .map((installment) => (
                            <TableRow key={installment.id}>
                              <TableCell>{installment.installmentNumber}</TableCell>
                              <TableCell>
                                {installment.dueDate ? new Date(installment.dueDate).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell>Rs {installment.amount?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={installment.status} 
                                  size="small"
                                  color={
                                    installment.status === 'PAID' ? 'success' :
                                    installment.status === 'PARTIAL' ? 'warning' :
                                    'default'
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  No repayment schedule available for rejected loans.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RejectedLoans;
